import React, { useMemo } from 'react';

import { Box } from '@mui/material';

interface Props {
  value: unknown;
  functionName?: string;
}

type JsonTokenKind = 'plain' | 'key' | 'string' | 'literal' | 'number';

const COLORS: Record<JsonTokenKind, string> = {
  plain: 'text.primary',
  key: '#ed4b99',
  string: '#008c35',
  literal: '#006bd6',
  number: '#ed4b99',
};

function tokenizeJson(json: string): Array<{ kind: JsonTokenKind; text: string }> {
  const tokens: Array<{ kind: JsonTokenKind; text: string }> = [];
  let plain = '';
  const flushPlain = () => {
    if (plain) {
      tokens.push({ kind: 'plain', text: plain });
    }
    plain = '';
  };

  for (let index = 0; index < json.length; ) {
    if (json[index] === '"') {
      let end = index + 1;
      while (end < json.length) {
        if (json[end] === '\\') {
          end += 2;
        } else if (json[end++] === '"') {
          break;
        }
      }
      flushPlain();
      let next = end;
      while (/\s/.test(json[next] ?? '')) {
        next += 1;
      }
      tokens.push({ kind: json[next] === ':' ? 'key' : 'string', text: json.slice(index, end) });
      index = end;
      continue;
    }
    const match = /^(true|false|null|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/.exec(json.slice(index));
    if (match) {
      flushPlain();
      tokens.push({ kind: /^(true|false|null)$/.test(match[1]) ? 'literal' : 'number', text: match[1] });
      index += match[1].length;
      continue;
    }
    plain += json[index++];
  }
  flushPlain();
  return tokens;
}

export default function ChatTraceJson({ value, functionName }: Props) {
  const segments = useMemo(() => {
    let parsed = value;
    if (typeof value === 'string') {
      try {
        parsed = JSON.parse(value);
      } catch {
        // Preserve non-JSON tool output as a JSON string rather than losing it.
      }
    }
    return tokenizeJson(JSON.stringify(parsed ?? null, null, 2));
  }, [value]);

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.65,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {functionName && `${functionName}(`}
      {segments.map((segment, index) => (
        <Box component="span" key={index} sx={{ color: COLORS[segment.kind] }}>
          {segment.text}
        </Box>
      ))}
      {functionName && ')'}
    </Box>
  );
}
